import React, { useState, useEffect } from 'react';

const DailyCheckinModal = ({ isOpen, onClose, checkinResult }) => {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen && checkinResult?.success) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, checkinResult]);

  if (!isOpen) return null;

  const getStreakMessage = (streak) => {
    if (streak === 1) return "Great start! 🌟";
    if (streak < 7) return `${streak} day streak! Keep it up! ⚡`;
    if (streak < 30) return `Amazing ${streak} day streak! You're on fire! 🔥`;
    return `Incredible ${streak} day streak! You're a legend! 🏆`;
  };

  const getStreakBadge = (streak) => {
    if (streak < 7) return "🌱 Growing";
    if (streak < 14) return "⚡ Consistent";
    if (streak < 30) return "🔥 On Fire";
    if (streak < 100) return "🏆 Champion";
    return "💎 Legend";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 relative overflow-hidden">
        {/* Confetti Effect */}
        {showConfetti && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-bounce"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: '1s'
                }}
              >
                {['🎉', '⭐', '✨', '🎊', '💫'][Math.floor(Math.random() * 5)]}
              </div>
            ))}
          </div>
        )}

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>

        {/* Content */}
        <div className="text-center">
          {checkinResult?.success ? (
            <>
              {/* Success Check-in */}
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{
                background: 'var(--mc-primary)'
              }}>
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>

              <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--mc-charcoal)' }}>
                Daily Check-in Complete! 🎉
              </h2>

              <p className="mb-6" style={{ color: 'var(--mc-gray-600)' }}>
                {getStreakMessage(checkinResult.streak)}
              </p>

              {/* Points Earned */}
              <div className="rounded-xl p-4 mb-6" style={{
                background: 'var(--mc-primary-lighter)',
                border: '1px solid var(--mc-primary-light)'
              }}>
                <div className="flex items-center justify-center">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mr-4" style={{
                    background: 'var(--mc-primary)'
                  }}>
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: 'var(--mc-gray-600)' }}>Points Earned</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--mc-primary)' }}>+{checkinResult.points} XP</p>
                  </div>
                </div>
              </div>

              {/* Streak Information */}
              <div className="rounded-xl p-4 mb-6" style={{
                background: 'var(--mc-primary-lighter)',
                border: '1px solid var(--mc-primary-light)'
              }}>
                <div className="flex items-center justify-center">
                  <div className="text-3xl mr-3">
                    {checkinResult.streak < 7 ? '⚡' : checkinResult.streak < 30 ? '🔥' : '🏆'}
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: 'var(--mc-gray-600)' }}>Current Streak</p>
                    <p className="text-xl font-bold" style={{ color: 'var(--mc-primary)' }}>
                      {checkinResult.streak} {checkinResult.streak === 1 ? 'Day' : 'Days'}
                    </p>
                    <p className="text-xs text-gray-500">{getStreakBadge(checkinResult.streak)}</p>
                  </div>
                </div>
              </div>

              {/* Streak Milestones */}
              {checkinResult.streak % 7 === 0 && checkinResult.streak > 0 && (
                <div className="rounded-xl p-4 mb-6 border-2" style={{
                  background: 'var(--mc-primary-lighter)',
                  borderColor: 'var(--mc-primary-light)'
                }}>
                  <div className="flex items-center justify-center">
                    <span className="text-2xl mr-2">🏅</span>
                    <div>
                      <p className="font-bold" style={{ color: 'var(--mc-primary)' }}>Streak Milestone!</p>
                      <p className="text-sm" style={{ color: 'var(--mc-gray-600)' }}>You earned bonus XP for your {checkinResult.streak}-day streak!</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Already Checked In or Error */}
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{
                background: 'var(--mc-primary)'
              }}>
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>

              <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--mc-charcoal)' }}>
                Already Checked In! ✅
              </h2>

              <p className="mb-6" style={{ color: 'var(--mc-gray-600)' }}>
                {checkinResult?.message || "You've already checked in today. Come back tomorrow for more points!"}
              </p>

              <div className="rounded-xl p-4 mb-6" style={{
                background: 'var(--mc-primary-lighter)',
                border: '1px solid var(--mc-primary-light)'
              }}>
                <p className="font-medium" style={{ color: 'var(--mc-primary)' }}>
                  💡 Keep practicing to earn more XP throughout the day!
                </p>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-medium transition-colors"
            >
              Close
            </button>
            
            {checkinResult?.success && (
              <button
                onClick={onClose}
                className="flex-1 text-white py-3 px-4 rounded-lg font-medium transition-all duration-200"
                style={{ background: 'var(--mc-primary)' }}
                onMouseEnter={(e) => e.target.style.background = 'var(--mc-primary-dark)'}
                onMouseLeave={(e) => e.target.style.background = 'var(--mc-primary)'}
              >
                Start Learning!
              </button>
            )}
          </div>
        </div>

        {/* Fun Facts Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            🎯 Daily check-ins help build consistent learning habits!
          </p>
        </div>
      </div>
    </div>
  );
};

export default DailyCheckinModal;